import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuGroup,
} from '@scholastic/mockingjay';

import { NavLink } from 'react-router-dom';
import { type ComponentPropsWithoutRef } from 'react';
import { useGetMeApi, useGetPermissionsApi } from 'eds-common/api/v2';
import { cn } from 'eds-common';
import BentoMenu from '@/assets/images/bento-menu.svg';
import ClassesIconLight from '@/assets/icons/classes-icon-light.svg';
import DataIconLight from '@/assets/icons/data-icon-light.svg';
import HomeIconLight from '@/assets/icons/home-light-icon.svg';
import SubscriptionsIconLight from '@/assets/icons/subscriptions-icon-light.svg';
import UserCircleIconLight from '@/assets/icons/user-circle-icon-light.svg';
import { useApplications } from '@/hooks/api/use-application/use-application';
import { APPLICATION_CODE_ROUTES } from '@/constants';
import { hasOnlyKlAccess } from '@/utils/application-utils';
import type { Application } from '@/services/composite-service/composite-service';

const PRODUCTS_LABEL_ID = 'platform-menu-products-label';

interface PlatformMenuItemProps
  extends ComponentPropsWithoutRef<typeof DropdownMenuItem> {
  linkTo: string;
}

function PlatformMenuItem({
  linkTo,
  children,
  disabled,
}: PlatformMenuItemProps) {
  return (
    <DropdownMenuItem className="p-0" disabled={disabled} asChild>
      <NavLink
        to={disabled ? '#' : linkTo}
        className={cn(
          'flex items-center gap-2 px-6 py-2 hover:bg-neutral-100 hover:text-neutral-1300 [&.active]:bg-neutral-1100 [&.active]:text-brand-white [&.active]:hover:bg-neutral-1200 [&.active]:hover:text-brand-white',
          disabled &&
            'cursor-not-allowed bg-brand-white text-neutral-700 hover:bg-brand-white hover:text-neutral-700'
        )}
      >
        {children}
      </NavLink>
    </DropdownMenuItem>
  );
}

export interface PlatformMenuProps {
  triggerClassName?: string;
}

export function PlatformMenu({ triggerClassName }: PlatformMenuProps) {
  const { data: permissions } = useGetPermissionsApi();
  const { data: user } = useGetMeApi();
  const { data: applications } = useApplications();

  const shownApplications = applications ?? [];

  const userHasOnlyKlAccess = hasOnlyKlAccess(shownApplications);
  const isStudent = user?.currentRole === 'student';

  const renderApplicationItem = (application: Application) => {
    const { applicationCode, applicationTitle, nativeApplication } =
      application;
    return nativeApplication ? (
      <PlatformMenuItem
        key={applicationCode}
        linkTo={APPLICATION_CODE_ROUTES[applicationCode] ?? '#'}
      >
        {applicationTitle}
      </PlatformMenuItem>
    ) : (
      <DropdownMenuItem
        key={applicationCode}
        className="flex items-center gap-2 px-6 hover:bg-neutral-100 hover:text-neutral-1300"
        asChild
      >
        <a href={`/api/auth/v2/launch/route/app/${applicationCode}`}>
          {applicationTitle}
        </a>
      </DropdownMenuItem>
    );
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger>
        <button
          type="button"
          className={cn(
            'flex items-center rounded-full bg-brand-white focus-ring hover:bg-neutral-100',
            triggerClassName
          )}
          aria-label="Platform"
        >
          <BentoMenu aria-hidden />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        collisionPadding={16}
        style={{
          maxHeight: 'var(--radix-dropdown-menu-content-available-height)',
          overflowY: 'auto',
        }}
      >
        <PlatformMenuItem linkTo="/">
          <HomeIconLight className="h-6 w-6" aria-hidden />
          Home
        </PlatformMenuItem>
        {!isStudent && (
          <>
            {!userHasOnlyKlAccess && (
              <PlatformMenuItem linkTo="/data">
                <DataIconLight className="h-6 w-6" aria-hidden />
                Data and Insights
              </PlatformMenuItem>
            )}
            {user?.userSource !== 'MyScholastic' && (
              <PlatformMenuItem linkTo="/classes">
                <ClassesIconLight className="h-6 w-6" aria-hidden />
                Classes
              </PlatformMenuItem>
            )}
            <PlatformMenuItem linkTo="/subscriptions">
              <SubscriptionsIconLight className="h-6 w-6" aria-hidden />
              Subscriptions
            </PlatformMenuItem>
            {permissions?.manageAllUsers && (
              <PlatformMenuItem linkTo="/users">
                <UserCircleIconLight className="h-6 w-6" aria-hidden />
                Users
              </PlatformMenuItem>
            )}
          </>
        )}
        {shownApplications.length > 0 && (
          <>
            <DropdownMenuLabel id={PRODUCTS_LABEL_ID} className="px-6">
              Products
            </DropdownMenuLabel>
            <DropdownMenuGroup
              aria-labelledby={PRODUCTS_LABEL_ID}
              className="max-h-[min(328px,45vh)] overflow-y-auto overflow-x-hidden [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-neutral-400 [&::-webkit-scrollbar-track]:bg-neutral-100 [&::-webkit-scrollbar]:w-2"
              data-testid="platform-menu-products"
            >
              {shownApplications.map((application) =>
                renderApplicationItem(application)
              )}
            </DropdownMenuGroup>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
